import { uniqueId } from '../../helpers/uniqueId'
import {
  MerchantApplicationState,
  MerchantApplicationStep,
  ConfirmInfoData,
  BusinessDetailsData,
  OwnerData,
  BankAccountData,
  DocumentUpload,
  PreFillData,
  STEP_COUNT,
} from './merchantApplicationModel'

// Actions

export type MerchantApplicationAction =
  | { type: 'SET_STEP'; payload: MerchantApplicationStep }
  | { type: 'NEXT_STEP' }
  | { type: 'PREV_STEP' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'INIT_FROM_PREFILL'; payload: PreFillData }
  // Step 1
  | { type: 'UPDATE_CONFIRM_INFO'; field: keyof ConfirmInfoData; value: string }
  // Step 2
  | { type: 'UPDATE_BUSINESS_DETAILS'; field: keyof BusinessDetailsData; value: string }
  // Step 3
  | { type: 'UPDATE_OWNER'; ownerId: string; field: keyof OwnerData; value: string | number | boolean }
  | { type: 'ADD_OWNER' }
  | { type: 'REMOVE_OWNER'; ownerId: string }
  // Step 4
  | { type: 'UPDATE_BANK_ACCOUNT'; field: keyof BankAccountData; value: string }
  // Step 5
  | { type: 'SET_DOCUMENT'; documentType: 'taxIdDocument' | 'identificationDocument' | 'bankDocument'; file: File | null }
  // Submit
  | { type: 'SET_SUBMITTING'; payload: boolean }
  | { type: 'SET_SUBMITTED' }

// Initial state

const emptyDocument: DocumentUpload = { file: null }

export const getInitialState = (): MerchantApplicationState => ({
  currentStep: 0 as MerchantApplicationStep,
  isLoading: true,
  confirmInfo: {
    firstName: '',
    lastName: '',
    email: '',
    businessName: '',
    businessDba: '',
    phoneNumber: '',
    website: '',
    address1: '',
    city: '',
    stateCode: '',
    zip: '',
  },
  businessDetails: {
    taxId: '',
    organizationType: '',
    averagePaymentAmount: '',
    maximumPaymentAmount: '',
    monthlyPaymentTotal: '',
  },
  owners: [],
  bankAccount: {
    bankName: '',
    routingNumber: '',
    accountNumber: '',
    confirmAccountNumber: '',
  },
  documents: {
    taxIdDocument: { ...emptyDocument },
    identificationDocument: { ...emptyDocument },
    bankDocument: { ...emptyDocument },
  },
  hasPaymentHistory: false,
  isSubmitting: false,
  isSubmitted: false,
})

// Reducer

export const merchantApplicationReducer = (
  state: MerchantApplicationState,
  action: MerchantApplicationAction
): MerchantApplicationState => {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStep: action.payload }

    case 'NEXT_STEP':
      return {
        ...state,
        currentStep: Math.min(STEP_COUNT - 1, state.currentStep + 1) as MerchantApplicationStep,
      }

    case 'PREV_STEP':
      return {
        ...state,
        currentStep: Math.max(0, state.currentStep - 1) as MerchantApplicationStep,
      }

    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }

    case 'INIT_FROM_PREFILL': {
      const hasHistory = action.payload.paymentEstimates.averagePayment !== null
      return {
        ...state,
        isLoading: false,
        hasPaymentHistory: hasHistory,
        confirmInfo: {
          firstName: action.payload.firstName,
          lastName: action.payload.lastName,
          email: action.payload.email,
          businessName: action.payload.businessName,
          businessDba: '',
          phoneNumber: action.payload.phoneNumber,
          website: action.payload.website,
          address1: action.payload.address1,
          city: action.payload.city,
          stateCode: action.payload.stateCode,
          zip: action.payload.zip,
        },
        businessDetails: {
          ...state.businessDetails,
          averagePaymentAmount: (action.payload.paymentEstimates.averagePayment ?? '').toString(),
          maximumPaymentAmount: (action.payload.paymentEstimates.maximumPayment ?? '').toString(),
          monthlyPaymentTotal: (action.payload.paymentEstimates.monthlyTotal ?? '').toString(),
        },
        owners: [
          {
            id: uniqueId(),
            firstName: action.payload.firstName,
            lastName: action.payload.lastName,
            email: action.payload.email,
            phone: action.payload.phoneNumber,
            dateOfBirth: '',
            ssn: '',
            ownershipPercentage: 100,
            useBusinessAddress: true,
            address1: action.payload.address1,
            city: action.payload.city,
            stateCode: action.payload.stateCode,
            zip: action.payload.zip,
          },
        ],
      }
    }

    case 'UPDATE_CONFIRM_INFO': {
      const newConfirmInfo = { ...state.confirmInfo, [action.field]: action.value }
      const ownerFieldMap: Partial<Record<keyof typeof newConfirmInfo, keyof typeof state.owners[0]>> = {
        firstName: 'firstName',
        lastName: 'lastName',
        email: 'email',
        phoneNumber: 'phone',
      }
      const addressFields: (keyof typeof newConfirmInfo)[] = ['address1', 'city', 'stateCode', 'zip']
      const ownerField = ownerFieldMap[action.field]
      const isAddressField = addressFields.includes(action.field)

      const shouldSyncOwner = ownerField || isAddressField
      if (!shouldSyncOwner || state.owners.length === 0) {
        return { ...state, confirmInfo: newConfirmInfo }
      }

      const updatedOwners = [...state.owners]
      const firstOwner = { ...updatedOwners[0] }
      if (ownerField) {
        (firstOwner as any)[ownerField] = action.value
      }
      if (isAddressField && firstOwner.useBusinessAddress) {
        (firstOwner as any)[action.field] = action.value
      }
      updatedOwners[0] = firstOwner

      return { ...state, confirmInfo: newConfirmInfo, owners: updatedOwners }
    }

    case 'UPDATE_BUSINESS_DETAILS':
      return {
        ...state,
        businessDetails: { ...state.businessDetails, [action.field]: action.value },
      }

    case 'UPDATE_OWNER':
      return {
        ...state,
        owners: state.owners.map(owner =>
          owner.id === action.ownerId
            ? { ...owner, [action.field]: action.value }
            : owner
        ),
      }

    case 'ADD_OWNER': {
      const totalOwnership = state.owners.reduce((sum, o) => sum + o.ownershipPercentage, 0)
      const remaining = Math.max(0, 100 - totalOwnership)
      return {
        ...state,
        owners: [
          ...state.owners,
          {
            id: uniqueId(),
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            dateOfBirth: '',
            ssn: '',
            ownershipPercentage: remaining,
            useBusinessAddress: true,
            address1: state.confirmInfo.address1,
            city: state.confirmInfo.city,
            stateCode: state.confirmInfo.stateCode,
            zip: state.confirmInfo.zip,
          },
        ],
      }
    }

    case 'REMOVE_OWNER':
      return {
        ...state,
        owners: state.owners.filter(o => o.id !== action.ownerId),
      }

    case 'UPDATE_BANK_ACCOUNT':
      return {
        ...state,
        bankAccount: { ...state.bankAccount, [action.field]: action.value },
      }

    case 'SET_DOCUMENT':
      return {
        ...state,
        documents: {
          ...state.documents,
          [action.documentType]: {
            file: action.file,
          },
        },
      }

    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.payload }

    case 'SET_SUBMITTED':
      return { ...state, isSubmitting: false, isSubmitted: true }

    default:
      return state
  }
}
